import { useState } from 'react';
import { motion } from 'framer-motion';
import { Truck, Plus, Search, Phone, Mail, MapPin, Edit3, Trash2, X, CheckCircle } from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatCurrency, generateId, isValidEmail, isValidPhone } from '../utils/helpers';
import type { Supplier } from '../types';

function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`fixed top-5 right-5 z-[100] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-lg text-[13px] font-medium ${
        type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      <CheckCircle className="w-4 h-4" />
      {message}
      <button onClick={onClose} className="ml-2 opacity-75 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </motion.div>
  );
}

const BLANK_FORM = { name: '', company: '', phone: '', email: '', address: '' };

export default function SuppliersPage() {
  const { suppliers, addSupplier, updateSupplier, deleteSupplier } = useAdminStore();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = suppliers.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.company.toLowerCase().includes(search.toLowerCase())
  );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Contact name is required';
    if (!form.company.trim()) e.company = 'Company name is required';
    if (!form.phone.trim()) e.phone = 'Phone is required';
    else if (!isValidPhone(form.phone)) e.phone = 'Invalid phone number';
    if (form.email && !isValidEmail(form.email)) e.email = 'Invalid email address';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openAdd = () => {
    setForm({ ...BLANK_FORM });
    setErrors({});
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (supplier: Supplier) => {
    setForm({
      name: supplier.name,
      company: supplier.company,
      phone: supplier.phone,
      email: supplier.email || '',
      address: supplier.address || '',
    });
    setErrors({});
    setEditId(supplier.id);
    setShowModal(true);
  };

  const handleSave = () => {
    if (!validate()) return;
    if (editId) {
      updateSupplier(editId, { ...form });
      showToast('Supplier updated successfully');
    } else {
      addSupplier({
        id: generateId(),
        ...form,
        products: [],
        totalPurchases: 0,
        outstandingBalance: 0,
        createdAt: new Date().toISOString(),
      });
      showToast('Supplier added successfully');
    }
    setShowModal(false);
  };

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(`Delete supplier "${name}"?`)) return;
    deleteSupplier(id);
    showToast('Supplier deleted', 'error');
  };

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Suppliers</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{suppliers.length} supplier partners</p>
        </div>
        <button onClick={openAdd} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2">
          <Plus className="w-4 h-4" />Add Supplier
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suppliers..."
          className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
      </div>

      {filtered.length === 0 && (
        <div className="bg-white/80 rounded-2xl border border-gray-200/60 p-12 text-center text-[13px] text-gray-400">
          No suppliers found. <button onClick={openAdd} className="text-gray-900 font-medium underline ml-1">Add your first supplier.</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((supplier, i) => (
          <motion.div key={supplier.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5 hover:shadow-lg hover:shadow-gray-200/40 transition-all">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-[14px] font-semibold">
                {supplier.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h3 className="text-[14px] font-semibold text-gray-900">{supplier.name}</h3>
                <p className="text-[12px] text-gray-400">{supplier.company}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(supplier)} className="p-1.5 rounded-lg hover:bg-gray-100"><Edit3 className="w-3.5 h-3.5 text-gray-400" /></button>
                <button onClick={() => handleDelete(supplier.id, supplier.name)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-[12px] text-gray-500"><Phone className="w-3.5 h-3.5 text-gray-400" />{supplier.phone}</div>
              {supplier.email && <div className="flex items-center gap-2 text-[12px] text-gray-500"><Mail className="w-3.5 h-3.5 text-gray-400" />{supplier.email}</div>}
              {supplier.address && <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin className="w-3.5 h-3.5 text-gray-400" />{supplier.address}</div>}
            </div>
            <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
              <div><span className="text-[11px] text-gray-400">Total Purchases</span><p className="text-[13px] font-semibold text-gray-900">{formatCurrency(supplier.totalPurchases)}</p></div>
              {supplier.outstandingBalance > 0 && (
                <div className="text-right"><span className="text-[11px] text-gray-400">Outstanding</span><p className="text-[13px] font-semibold text-red-500">{formatCurrency(supplier.outstandingBalance)}</p></div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-semibold text-gray-900">{editId ? 'Edit Supplier' : 'Add Supplier'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Contact Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className={`w-full h-10 px-3 rounded-xl bg-gray-50 border text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 ${errors.name ? 'border-red-300' : 'border-gray-200/60'}`} />
                {errors.name && <p className="text-red-500 text-[11px] mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Company *</label>
                <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                  className={`w-full h-10 px-3 rounded-xl bg-gray-50 border text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 ${errors.company ? 'border-red-300' : 'border-gray-200/60'}`} />
                {errors.company && <p className="text-red-500 text-[11px] mt-1">{errors.company}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Phone *</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    className={`w-full h-10 px-3 rounded-xl bg-gray-50 border text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 ${errors.phone ? 'border-red-300' : 'border-gray-200/60'}`} />
                  {errors.phone && <p className="text-red-500 text-[11px] mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Email</label>
                  <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    className={`w-full h-10 px-3 rounded-xl bg-gray-50 border text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 ${errors.email ? 'border-red-300' : 'border-gray-200/60'}`} />
                  {errors.email && <p className="text-red-500 text-[11px] mt-1">{errors.email}</p>}
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Address</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleSave} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800">
                {editId ? 'Update Supplier' : 'Add Supplier'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
